import { useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Sparkles } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import confetti from "canvas-confetti";

interface AvailabilityConfirmationProps {
  weddingDate: Date;
  onNext: () => void;
}

const AvailabilityConfirmation = ({ weddingDate, onNext }: AvailabilityConfirmationProps) => {
  const daysUntilWedding = differenceInDays(weddingDate, new Date());
  const formattedDate = format(weddingDate, "MMMM d, yyyy");

  useEffect(() => {
    // Fire confetti from both sides
    const duration = 2000;
    const end = Date.now() + duration;

    const colors = ['#c9a8a8', '#e8d5d5', '#f5ebe0', '#d4a574'];

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        colors: colors
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        colors: colors
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    // Initial burst
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: colors
    });

    frame();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex flex-col items-center text-center"
    >
      {/* Celebratory Animation */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
        className="relative mb-6"
      >
        <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
          <CheckCircle2 className="w-14 h-14 text-primary" />
        </div>
        
        {/* Sparkle decorations */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-primary" />
          <Sparkles className="absolute -bottom-1 -left-3 w-5 h-5 text-primary/70" />
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-2">
          Great news!
        </h2>
        <p className="text-xl text-primary font-medium mb-4">
          We're available on {formattedDate}!
        </p>
        <p className="text-muted-foreground mb-2">
          That's {daysUntilWedding} days from now — how exciting!
        </p>
        <p className="text-muted-foreground mb-8">
          We would love to be part of your special day. Let's get to know you better.
        </p>
      </motion.div>

      <Button
        onClick={onNext}
        size="lg"
        className="w-full max-w-xs"
      >
        Let's Continue
      </Button>
    </motion.div>
  );
};

export default AvailabilityConfirmation;
